import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiArrowRight, FiMove, FiAlertTriangle, FiCheck, FiCalendar, FiLoader } = FiIcons;

const MenuSwapModal = ({ 
  isOpen, 
  onClose, 
  sourceMenu, 
  availableDays, 
  existingMenus, 
  onSwap, 
  isLoading 
}) => {
  const [selectedTargetDay, setSelectedTargetDay] = useState('');

  if (!isOpen || !sourceMenu) return null;

  const handleSwap = () => {
    if (selectedTargetDay) {
      onSwap(selectedTargetDay);
      setSelectedTargetDay('');
    }
  };

  const targetMenu = existingMenus[selectedTargetDay];
  const isSwapWithExisting = targetMenu && targetMenu.id !== sourceMenu.id;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-800 flex items-center">
            <SafeIcon icon={FiMove} className="mr-2" />
            Menü verschieben
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            disabled={isLoading}
          >
            <SafeIcon icon={FiX} />
          </button>
        </div>

        {/* Source Menu Info */}
        <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
          <h3 className="font-semibold text-blue-800 mb-2">Menü das verschoben wird:</h3>
          <div className="flex items-center space-x-3">
            <img 
              src={sourceMenu.image} 
              alt={sourceMenu.name} 
              className="w-16 h-16 rounded-lg object-cover"
            />
            <div>
              <div className="font-medium text-gray-800">{sourceMenu.name}</div>
              <div className="text-sm text-gray-600">
                Aktuell: {sourceMenu.day.charAt(0).toUpperCase() + sourceMenu.day.slice(1)}
              </div>
              <div className="text-sm font-medium text-blue-600">
                CHF {sourceMenu.price?.toFixed(2)}
              </div>
            </div>
          </div>
        </div>

        {/* Target Day Selection */}
        <div className="mb-6">
          <h3 className="font-semibold text-gray-800 mb-4">Ziel-Tag auswählen:</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {availableDays.map(day => {
              const dayDisplayName = day.charAt(0).toUpperCase() + day.slice(1);
              const existingMenu = existingMenus[day];
              const isSourceDay = day === sourceMenu.day;
              const isSelected = selectedTargetDay === day;

              return (
                <button
                  key={day}
                  onClick={() => !isSourceDay ? setSelectedTargetDay(day) : null}
                  disabled={isSourceDay || isLoading}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    isSourceDay 
                      ? 'border-gray-200 bg-gray-100 cursor-not-allowed opacity-50' 
                      : isSelected
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-primary-300 hover:bg-primary-25'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-800">
                      {dayDisplayName}
                    </span>
                    {isSourceDay && (
                      <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full">
                        Aktuell
                      </span>
                    )}
                  </div>
                  
                  {existingMenu && existingMenu.id !== sourceMenu.id ? (
                    <div className="text-sm">
                      <div className="text-gray-600 truncate">{existingMenu.name}</div>
                      <div className="text-primary-600 font-medium">
                        CHF {existingMenu.price?.toFixed(2)}
                      </div>
                      <div className="text-orange-600 text-xs mt-1 flex items-center">
                        <SafeIcon icon={FiAlertTriangle} className="mr-1" />
                        Wird getauscht
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">
                      {isSourceDay ? 'Aktueller Platz' : 'Leer'}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Swap Preview */}
        {selectedTargetDay && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-green-50 rounded-xl border border-green-200"
          >
            <h3 className="font-semibold text-green-800 mb-3 flex items-center">
              <SafeIcon icon={FiCheck} className="mr-2" />
              Vorschau der Änderung:
            </h3>
            
            <div className="space-y-3">
              <div className="flex items-center space-x-4">
                <div className="flex-1 text-center p-3 bg-white rounded-lg">
                  <div className="font-medium text-gray-800">{sourceMenu.name}</div>
                  <div className="text-sm text-gray-600">
                    {sourceMenu.day.charAt(0).toUpperCase() + sourceMenu.day.slice(1)}
                  </div>
                </div>
                
                <SafeIcon icon={FiArrowRight} className="text-green-600" />
                
                <div className="flex-1 text-center p-3 bg-white rounded-lg">
                  <div className="font-medium text-gray-800">{sourceMenu.name}</div>
                  <div className="text-sm text-green-600">
                    {selectedTargetDay.charAt(0).toUpperCase() + selectedTargetDay.slice(1)}
                  </div>
                </div>
              </div>

              {isSwapWithExisting && (
                <div className="flex items-center space-x-4">
                  <div className="flex-1 text-center p-3 bg-white rounded-lg">
                    <div className="font-medium text-gray-800">{targetMenu.name}</div>
                    <div className="text-sm text-gray-600">
                      {selectedTargetDay.charAt(0).toUpperCase() + selectedTargetDay.slice(1)}
                    </div>
                  </div>
                  
                  <SafeIcon icon={FiArrowRight} className="text-green-600" />
                  
                  <div className="flex-1 text-center p-3 bg-white rounded-lg">
                    <div className="font-medium text-gray-800">{targetMenu.name}</div>
                    <div className="text-sm text-green-600">
                      {sourceMenu.day.charAt(0).toUpperCase() + sourceMenu.day.slice(1)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Action Buttons */}
        <div className="flex space-x-4">
          <motion.button
            onClick={handleSwap}
            disabled={!selectedTargetDay || isLoading}
            whileHover={{ scale: selectedTargetDay && !isLoading ? 1.02 : 1 }}
            whileTap={{ scale: selectedTargetDay && !isLoading ? 0.98 : 1 }}
            className={`flex-1 flex items-center justify-center space-x-2 py-3 rounded-xl font-semibold transition-colors ${
              selectedTargetDay && !isLoading
                ? 'bg-primary-500 text-white hover:bg-primary-600'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isLoading ? (
              <>
                <SafeIcon icon={FiLoader} className="animate-spin" />
                <span>Wird verschoben...</span>
              </>
            ) : (
              <>
                <SafeIcon icon={isSwapWithExisting ? FiMove : FiArrowRight} />
                <span>
                  {isSwapWithExisting ? 'Menüs tauschen' : 'Menü verschieben'}
                </span>
              </>
            )}
          </motion.button>

          <motion.button
            onClick={onClose}
            disabled={isLoading}
            whileHover={{ scale: !isLoading ? 1.02 : 1 }}
            whileTap={{ scale: !isLoading ? 0.98 : 1 }}
            className="flex-1 flex items-center justify-center space-x-2 bg-gray-200 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-300 transition-colors"
          >
            <SafeIcon icon={FiX} />
            <span>Abbrechen</span>
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

export default MenuSwapModal;